import { Link, useParams } from "react-router-dom";
import { Check } from "lucide-react";
import { Gallery, Facts } from "@/pages/Detail";
import { ShareButtons } from "@/components/site/ShareButtons";
import { WhatsAppButton } from "@/components/site/Contact";
import { CurrencySwitcher } from "@/components/site/CurrencySwitcher";
import { Price } from "@/context/CurrencyContext";
import { BookingWidget } from "@/components/site/BookingWidget";
import { naira } from "@/components/site/ShortletCard";
import { useItem, usePageMeta } from "@/hooks/useData";

const List = ({ title, items, testId }) =>
  items?.length > 0 ? (
    <div className="mt-8" data-testid={testId}>
      <p className="lab opacity-70">{title}</p>
      <ul className="mt-3 grid sm:grid-cols-2 gap-2">
        {items.map((a) => <li key={a} className="flex gap-2 items-start"><Check size={16} className="mt-1 shrink-0 opacity-60" />{a}</li>)}
      </ul>
    </div>
  ) : null;

export default function ShortletDetail() {
  const { id } = useParams();
  const s = useItem("shortlets", id);
  usePageMeta(s ? `${s.title}${s.location ? `, ${s.location}` : ""} | Wolfe Realtors Shortlets` : "Wolfe Realtors Shortlets",
    s ? `Shortlet apartment${s.nightly_rate ? ` from ${naira(s.nightly_rate)} a night` : ""}. ${s.description || ""}`.slice(0, 158) : undefined,
    s ? `https://www.greywolfeng.com/api/share/shortlet/${id}/image.jpg` : undefined);

  if (s === undefined) return <section className="bg-gw-paper min-h-[70vh]" />;
  if (s === null)
    return (
      <section className="bg-gw-paper text-gw-ink min-h-[70vh] py-32" data-testid="shortlet-not-found">
        <div className="max-w-6xl mx-auto px-6">
          <h1 className="lux gw-h2">This apartment is no longer listed.</h1>
          <Link to="/shortlets" className="lab mt-8 inline-block underline underline-offset-4">← All shortlets</Link>
        </div>
      </section>
    );

  const rows = [["Location", s.location], ["Bedrooms", s.bedrooms], ["Bathrooms", s.bathrooms], ["Sleeps", s.max_guests],
    ["Minimum stay", `${s.min_nights} night${s.min_nights > 1 ? "s" : ""}`], ["Cleaning fee", s.cleaning_fee ? naira(s.cleaning_fee) : null]];
  return (
    <div className="page-in">
      <section className="bg-gw-paper text-gw-ink py-16 md:py-24 border-t-8 border-gw-rule" data-testid="shortlet-detail-page">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-wrap justify-between gap-4 lab border-b border-gw-rule/25 pb-4">
            <Link to="/shortlets" data-testid="shortlet-back-link" className="hover:underline underline-offset-4">← Shortlets</Link>
            {s.is_sample && <span data-testid="shortlet-sample-note">Sample listing — replace from admin</span>}
          </div>
          <div className="grid lg:grid-cols-[1.2fr_1fr] gap-12 mt-12">
            <div className="min-w-0">
              <Gallery images={s.images} title={s.title} />
              <p className="lab opacity-70 mt-10">Shortlet apartment</p>
              <h1 className="lux gw-h2 mt-3" data-testid="shortlet-title">{s.title}</h1>
              {s.nightly_rate > 0 && (
                <div className="mt-4 flex flex-wrap items-end gap-6">
                  <p className="lux text-3xl"><Price price={naira(s.nightly_rate)} testId="shortlet-rate" /> <span className="text-base opacity-70">/ night</span></p>
                  <CurrencySwitcher testPrefix="shortlet-currency" />
                </div>
              )}
              <Facts rows={rows} />
              {s.description && <p className="gw-body mt-8 whitespace-pre-line" data-testid="shortlet-description">{s.description}</p>}
              <List title="Amenities" items={s.amenities} testId="shortlet-amenities" />
              <List title="House rules" items={s.house_rules} testId="shortlet-rules" />
              <div className="flex flex-wrap items-center gap-4 mt-8">
                <ShareButtons kind="shortlet" id={s.id} testPrefix="shortlet-share" text={`${s.title} — Wolfe Realtors shortlet`} />
                <WhatsAppButton testId="shortlet-whatsapp-button" text={`Hello, I'd like to ask about the shortlet ${s.title}.`}
                  className="lab px-5 py-3 border border-gw-rule/40 transition-colors duration-200 hover:bg-gw-hover" />
              </div>
            </div>
            <div className="min-w-0 lg:sticky lg:top-24 self-start">
              <p className="lab text-gw-accent mb-3">Book this apartment</p>
              <BookingWidget s={s} />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
